import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createWarehouseApi,
  createVendorApi,
  createRawMaterialApi,
  fetchWarehousesApi,
  fetchVendorsApi,
  fetchRawMaterialsApi,
  updateWarehouseApi,
  updateVendorApi,
  updateRawMaterialApi,
  createOutwardProductRequestApi,
  fetchWarehouseListApi,
  fetchWarehouseNamesAndIdsApi,
  deleteWarehouseVendorRawmaterialItemApi,
  fetchStoreInwardHistoryApi,
  fetchWarehouseMaterialsApi,
  adjustStockApi,
  fetchAdjustableStockApi,
  createOutwardMaterialRequestApi,
  updateOrderProductMaterialApi,
  fetchStockReductionLogsRawmaterialApi,
} from "../../api/warehouse.api";
import {
  IWarehouseRecord,
  WarehouseFormData,
  WarehouseUpdateData,
} from "../../models/warehouse.model";
import { IWarehouseRawMaterial } from "../../models/warehouseRawMaterial.model";
import { IVendor } from "../../models/vendor.model";
import { StockReductionLog } from "../../models/stockReductionLog.model";
import { PaginationRes } from "../../models/response.model";

interface WarehouseState {
  warehouses: IWarehouseRecord[];
  vendors: IVendor[];
  rawMaterials: IWarehouseRawMaterial[];
  stockReductionLogs: StockReductionLog[];
  stockReductionPagination: PaginationRes | null;
  stockReductionLoading: boolean;
  totalWarehouseItems: number;
  totalWarehousePages: number;
  totalVendorItems: number;
  totalVendorPages: number;
  totalRawMaterialItems: number;
  totalRawMaterialPages: number;
  warehouseNamesAndIds: { _id: string; name: string }[];
  loading: boolean;
  error: string | null;
  storeInwardHistory: any[];
  totalHistoryItems: number;
  totalHistoryPages: number;
  warehouseMaterials: {
    _id: string;
    sku: string;
    unit: string;
    unitType: string;
    name: string;
  }[];
}

const initialState: WarehouseState = {
  warehouses: [],
  vendors: [],
  rawMaterials: [],
  stockReductionLogs: [],
  stockReductionPagination: null,
  stockReductionLoading: false,
  totalWarehouseItems: 0,
  totalWarehousePages: 0,
  totalVendorItems: 0,
  totalVendorPages: 0,
  totalRawMaterialItems: 0,
  totalRawMaterialPages: 0,
  warehouseNamesAndIds: [],
  loading: false,
  error: null,
  storeInwardHistory: [],
  totalHistoryItems: 0,
  totalHistoryPages: 0,
  warehouseMaterials: [],
};

export const fetchStoreInwardHistoryThunk = createAsyncThunk<
  {
    history: any[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  },
  { productId: string; page: number; limit: number },
  { rejectValue: string }
>(
  "warehouse/fetchStoreInwardHistory",
  async ({ productId, page, limit }, { rejectWithValue }) => {
    try {
      const response = await fetchStoreInwardHistoryApi(productId, page, limit);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch history");
    }
  },
);

export const createWarehouseThunk = createAsyncThunk<
  IWarehouseRecord,
  WarehouseFormData,
  { rejectValue: string }
>("warehouse/createWarehouse", async (formData, { rejectWithValue }) => {
  try {
    const response = await createWarehouseApi(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create warehouse");
  }
});

export const fetchWarehousesThunk = createAsyncThunk<
  { data: IWarehouseRecord[]; totalItems: number; totalPages: number },
  { page: number; limit: number; search?: string },
  { rejectValue: string }
>(
  "warehouse/fetchWarehouses",
  async ({ page, limit, search }, { rejectWithValue }) => {
    try {
      const response = await fetchWarehousesApi({ page, limit, search });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch warehouses");
    }
  },
);

export const createVendorThunk = createAsyncThunk<
  IVendor,
  { name: string; gstNumber: string; contactNumber: string; address: string },
  { rejectValue: string }
>("warehouse/createVendor", async (formData, { rejectWithValue }) => {
  try {
    const response = await createVendorApi(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create vendor");
  }
});

export const fetchVendorsThunk = createAsyncThunk<
  { data: IVendor[]; totalItems: number; totalPages: number },
  { page: number; limit: number; search?: string },
  { rejectValue: string }
>(
  "warehouse/fetchVendors",
  async ({ page, limit, search }, { rejectWithValue }) => {
    try {
      const response = await fetchVendorsApi({ page, limit, search });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vendors");
    }
  },
);

export const updateOrderProductMaterialThunk = createAsyncThunk<
  { success: boolean; item: any; orderId: string },
  {
    orderId: string;
    id?: string;
    qty?: number;
    oldId?: string;
    oldQty?: number;
    type?: string;
    bomId?: string;
    oldBomId?: string | null;
  },
  { rejectValue: string }
>(
  "warehouse/updateOrderProductMaterial",
  async (
    { orderId, id, qty, oldId, oldQty, type, bomId, oldBomId },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateOrderProductMaterialApi({
        orderId,
        id,
        qty,
        oldId,
        oldQty,
        type,
        bomId,
        oldBomId,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update orders");
    }
  },
);

export const createRawMaterialThunk = createAsyncThunk<
  IWarehouseRawMaterial,
  {
    type: string;
    name: string;
    sku: string;
    unit: string;
    usageType: string;
    approxDeliveryDays: number;
    gst: number;
    hsn: string;
    requireSerialNumber: boolean;
  },
  { rejectValue: string }
>("warehouse/createRawMaterial", async (formData, { rejectWithValue }) => {
  try {
    const response = await createRawMaterialApi(formData);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create raw material");
  }
});

export const fetchWarehouseMaterialsThunk = createAsyncThunk<
  { _id: string; name: string; unit: string; unitType: string; sku: string }[],
  string,
  { rejectValue: string }
>(
  "warehouse/fetchWarehouseMaterials",
  async (usageType, { rejectWithValue }) => {
    try {
      const response = await fetchWarehouseMaterialsApi(usageType);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch materials");
    }
  },
);

export const fetchRawMaterialsThunk = createAsyncThunk<
  { data: IWarehouseRawMaterial[]; totalItems: number; totalPages: number },
  {
    page: number;
    limit: number;
    search?: string;
    bom?: boolean;
    sortBy?: string;
    sortOrder?: string;
    from: string;
  },
  { rejectValue: string }
>(
  "warehouse/fetchRawMaterials",
  async (
    { page, limit, search, bom = false, sortBy, sortOrder, from },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchRawMaterialsApi({
        page,
        limit,
        search,
        bom,
        sortBy,
        sortOrder,
        from,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch raw materials");
    }
  },
);

export const updateWarehouseThunk = createAsyncThunk<
  IWarehouseRecord,
  {
    id: string;
    data: WarehouseUpdateData;
  },
  { rejectValue: string }
>("warehouse/updateWarehouse", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateWarehouseApi(id, data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update warehouse");
  }
});

export const updateVendorThunk = createAsyncThunk<
  IVendor,
  {
    id: string;
    data: Partial<{
      name: string;
      gstNumber: string;
      contactNumber: string;
      address: string;
    }>;
  },
  { rejectValue: string }
>("warehouse/updateVendor", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateVendorApi(id, data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update vendor");
  }
});

export const updateRawMaterialThunk = createAsyncThunk<
  IWarehouseRawMaterial,
  {
    id: string;
    data: Partial<{
      type: string;
      name: string;
      description: string;
      sku: string;
      unit: string;
      usageType: string;
      minimumQuantity: number;
      approxDeliveryDays: number;
      gst: number;
      hsn: string;
      requireSerialNumber: boolean;
    }>;
  },
  { rejectValue: string }
>("warehouse/updateRawMaterial", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateRawMaterialApi(id, data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update raw material");
  }
});

export const createOutwardProductRequest = createAsyncThunk(
  "products/createOutwardProductRequest",
  async (
    products: {
      productId: string;
      warehouseId: string;
      serialNumber: string;
      loggerSerialNumber: string;
      manufacturingSerialNumber: string;
    }[],
    { rejectWithValue },
  ) => {
    try {
      const response = await createOutwardProductRequestApi(products);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const createOutwardMaterialRequest = createAsyncThunk(
  "products/createOutwardMaterialRequest",
  async (
    materials: {
      materialId: string;
      warehouseId: string;
      quantity: number;
      unit: string;
    }[],
    { rejectWithValue },
  ) => {
    try {
      const response = await createOutwardMaterialRequestApi(materials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const fetchWarehouseList = createAsyncThunk(
  "warehouse/fetchWarehouseList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchWarehouseListApi();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || "Failed to fetch warehouse list",
      );
    }
  },
);

export const fetchWarehouseNamesAndIdsThunk = createAsyncThunk<
  { _id: string; name: string }[],
  void,
  { rejectValue: string }
>("warehouse/fetchWarehouseNamesAndIds", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchWarehouseNamesAndIdsApi();
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.message || "Failed to fetch warehouse names and IDs",
    );
  }
});

export const fetchStockReductionLogs = createAsyncThunk(
  "epc/fetchStockReductionLogs",
  async (
    params: {
      search?: string;
      page: number;
      limit?: number;
      rawMaterialId?: string;
      warehouseId?: string;
      productId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchStockReductionLogsRawmaterialApi(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        (error as any).response?.data || "Failed to fetch reduction logs",
      );
    }
  },
);

export const fetchAdjustableStockThunk = createAsyncThunk<
  { qty: number; price: number; unit: string },
  { itemId: string; warehouseId: string; type: string },
  { rejectValue: string }
>(
  "warehouse/fetchAdjustableStock",
  async ({ itemId, warehouseId, type }, { rejectWithValue }) => {
    try {
      const response = await fetchAdjustableStockApi(itemId, warehouseId, type);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch stock data");
    }
  },
);

export const adjustStockThunk = createAsyncThunk<
  any,
  {
    itemId: string;
    type: "product" | "material";
    warehouseId: string;
    adjustmentQty: number;
    adjustmentType: "increase" | "decrease";
    unit: string;
  },
  { rejectValue: string }
>("warehouse/adjustStock", async (payload, { rejectWithValue }) => {
  try {
    const response = await adjustStockApi(payload);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        error.message ||
        "Failed to adjust stock",
    );
  }
});

export const deleteWarehouseVendorRawmaterialItemThunk = createAsyncThunk<
  { id: string; type: "factory" | "vendor" | "material" },
  { id: string; type: "factory" | "vendor" | "material" },
  { rejectValue: string }
>("warehouse/deleteItem", async ({ id, type }, { rejectWithValue }) => {
  try {
    await deleteWarehouseVendorRawmaterialItemApi(id, type);
    return { id, type };
  } catch (error: any) {
    return rejectWithValue(error.message || `Failed to delete ${type}`);
  }
});

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState,
  reducers: {
    resetStockReductionLogs: (state) => {
      state.stockReductionLogs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createWarehouseThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWarehouseThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses.push(action.payload);
        state.totalWarehouseItems += 1;
      })
      .addCase(createWarehouseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWarehousesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehousesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses = action.payload.data || [];
        state.totalWarehouseItems = action.payload.totalItems;
        state.totalWarehousePages = action.payload.totalPages;
      })
      .addCase(fetchWarehousesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createVendorThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVendorThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors.push(action.payload);
        state.totalVendorItems += 1;
      })
      .addCase(createVendorThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchVendorsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload.data || [];
        state.totalVendorItems = action.payload.totalItems;
        state.totalVendorPages = action.payload.totalPages;
      })
      .addCase(fetchVendorsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrderProductMaterialThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderProductMaterialThunk.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateOrderProductMaterialThunk.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload as string;
      })
      .addCase(createRawMaterialThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRawMaterialThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.rawMaterials.push(action.payload);
        state.totalRawMaterialItems += 1;
      })
      .addCase(createRawMaterialThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchRawMaterialsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRawMaterialsThunk.fulfilled, (state, action) => {
        state.rawMaterials = action.payload.data;
        state.totalRawMaterialItems = action.payload.totalItems;
        state.totalRawMaterialPages = action.payload.totalPages;
        state.loading = false;
      })
      .addCase(fetchRawMaterialsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateWarehouseThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWarehouseThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.warehouses.findIndex(
          (w) => w._id === action.payload._id,
        );
        if (index !== -1) state.warehouses[index] = action.payload;
      })
      .addCase(updateWarehouseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateVendorThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVendorThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.vendors.findIndex(
          (v) => v._id === action.payload._id,
        );
        if (index !== -1) state.vendors[index] = action.payload;
      })
      .addCase(updateVendorThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRawMaterialThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRawMaterialThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rawMaterials.findIndex(
          (m) => m._id === action.payload._id,
        );
        if (index !== -1) state.rawMaterials[index] = action.payload;
      })
      .addCase(updateRawMaterialThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createOutwardProductRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOutwardProductRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createOutwardProductRequest.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createOutwardMaterialRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOutwardMaterialRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createOutwardMaterialRequest.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchWarehouseList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseList.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses = action.payload || [];
      })
      .addCase(fetchWarehouseList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchStockReductionLogs.pending, (state) => {
        state.stockReductionLoading = true;
        state.error = null;
      })
      .addCase(fetchStockReductionLogs.fulfilled, (state, action) => {
        state.stockReductionLogs = action.payload.data;
        state.stockReductionPagination = action.payload.pagination || null;
        state.stockReductionLoading = false;
      })
      .addCase(fetchStockReductionLogs.rejected, (state, action) => {
        state.stockReductionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWarehouseNamesAndIdsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseNamesAndIdsThunk.fulfilled, (state, action) => {
        state.warehouseNamesAndIds = action.payload;
        state.loading = false;
      })
      .addCase(fetchWarehouseNamesAndIdsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteWarehouseVendorRawmaterialItemThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreInwardHistoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreInwardHistoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.storeInwardHistory = action.payload.history || [];
        state.totalHistoryItems = action.payload.pagination?.total || 0;
        state.totalHistoryPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchStoreInwardHistoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        deleteWarehouseVendorRawmaterialItemThunk.fulfilled,
        (state, action) => {
          state.loading = false;
          const { id, type } = action.payload;

          if (type === "factory") {
            state.warehouses = state.warehouses.filter(
              (item) => item._id !== id,
            );
            state.totalWarehouseItems -= 1;
          } else if (type === "vendor") {
            state.vendors = state.vendors.filter((item) => item._id !== id);
            state.totalVendorItems -= 1;
          } else if (type === "material") {
            state.rawMaterials = state.rawMaterials.filter(
              (item) => item._id !== id,
            );
            state.totalRawMaterialItems -= 1;
          }
        },
      )
      .addCase(
        deleteWarehouseVendorRawmaterialItemThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      )
      .addCase(fetchWarehouseMaterialsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseMaterialsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouseMaterials = action.payload;
      })
      .addCase(fetchWarehouseMaterialsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAdjustableStockThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdjustableStockThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAdjustableStockThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(adjustStockThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adjustStockThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(adjustStockThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});
export const { resetStockReductionLogs } = warehouseSlice.actions;
export default warehouseSlice.reducer;
